import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '4px 0 0 0',
  },
  svgContainer: {
    width: '100%',
    overflow: 'visible',
  },
  tooltip: {
    position: 'absolute',
    backgroundColor: '#1f2937',
    color: '#f9fafb',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '12px',
    lineHeight: '1.5',
    pointerEvents: 'none',
    zIndex: 1000,
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    whiteSpace: 'nowrap',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#9ca3af',
    fontSize: '14px',
  },
  legend: {
    display: 'flex',
    gap: '16px',
    marginTop: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#4b5563',
  },
  legendSwatch: {
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    flexShrink: 0,
  },
};

const TYPE_COLORS = {
  outlier: '#f59e0b',
  missing: '#6b7280',
  spike: '#dc2626',
};

const TYPE_LABELS = {
  outlier: 'Outlier',
  missing: 'Missing',
  spike: 'Spike',
};

function getTrendBadge(trendSlope) {
  if (trendSlope == null) return null;
  if (trendSlope > 0.05) {
    return { label: 'Accelerating', color: '#dc2626', bg: '#fef2f2' };
  }
  if (trendSlope < -0.05) {
    return { label: 'Improving', color: '#16a34a', bg: '#f0fdf4' };
  }
  return { label: 'Stable', color: '#d97706', bg: '#fffbeb' };
}

export default function CumulativeAnomalyChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const isValid = useMemo(() => {
    return (
      data &&
      Array.isArray(data.dates) &&
      data.dates.length > 0 &&
      Array.isArray(data.cumulativeCount) &&
      Array.isArray(data.rate) &&
      data.byType
    );
  }, [data]);

  const trendBadge = useMemo(() => {
    if (!isValid) return null;
    return getTrendBadge(data.trendSlope);
  }, [data, isValid]);

  useEffect(() => {
    if (!isValid || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 320;
    const margin = { top: 30, right: 60, bottom: 50, left: 50 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const { dates, cumulativeCount, rate, byType, trendSlope } = data;
    const n = dates.length;

    const parsedDates = dates.map((d) => new Date(d));
    const typeKeys = Object.keys(byType);

    // Scales
    const xScale = d3
      .scaleBand()
      .domain(d3.range(n))
      .range([0, innerWidth])
      .padding(0.25);

    const maxCumulative = d3.max(cumulativeCount) || 1;
    const yLeftScale = d3
      .scaleLinear()
      .domain([0, maxCumulative * 1.1])
      .range([innerHeight, 0])
      .nice();

    const maxRate = d3.max(rate) || 1;
    const yRightScale = d3
      .scaleLinear()
      .domain([0, maxRate * 1.2])
      .range([innerHeight, 0])
      .nice();

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Stepped area for cumulative count
    const areaPoints = [];
    for (let i = 0; i < n; i++) {
      const xPos = xScale(i) + xScale.bandwidth() / 2;
      areaPoints.push({ x: xPos, y: yLeftScale(cumulativeCount[i]) });
    }

    // Build stepped path data
    let areaPath = `M ${areaPoints[0].x} ${innerHeight}`;
    areaPath += ` L ${areaPoints[0].x} ${areaPoints[0].y}`;
    for (let i = 1; i < areaPoints.length; i++) {
      areaPath += ` L ${areaPoints[i].x} ${areaPoints[i - 1].y}`;
      areaPath += ` L ${areaPoints[i].x} ${areaPoints[i].y}`;
    }
    areaPath += ` L ${areaPoints[areaPoints.length - 1].x} ${innerHeight} Z`;

    g.append('path')
      .attr('d', areaPath)
      .attr('fill', '#93c5fd')
      .attr('fill-opacity', 0.35)
      .attr('stroke', 'none');

    // Stepped line on top of area
    let linePath = `M ${areaPoints[0].x} ${areaPoints[0].y}`;
    for (let i = 1; i < areaPoints.length; i++) {
      linePath += ` L ${areaPoints[i].x} ${areaPoints[i - 1].y}`;
      linePath += ` L ${areaPoints[i].x} ${areaPoints[i].y}`;
    }

    g.append('path')
      .attr('d', linePath)
      .attr('fill', 'none')
      .attr('stroke', '#3b82f6')
      .attr('stroke-width', 2.5);

    // Trend line overlaid on cumulative area
    if (trendSlope != null && n > 1) {
      const xMean = (n - 1) / 2;
      const yMean = d3.mean(cumulativeCount);
      const trendY = (i) => yMean + trendSlope * (i - xMean);

      const x0 = xScale(0) + xScale.bandwidth() / 2;
      const x1 = xScale(n - 1) + xScale.bandwidth() / 2;
      const y0 = yLeftScale(trendY(0));
      const y1 = yLeftScale(trendY(n - 1));

      g.append('line')
        .attr('x1', x0)
        .attr('y1', y0)
        .attr('x2', x1)
        .attr('y2', y1)
        .attr('stroke', '#6366f1')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '6,4')
        .attr('opacity', 0.8);
    }

    // Stacked bars for per-period rate by type
    const stackData = d3.range(n).map((i) => {
      const entry = { index: i };
      typeKeys.forEach((key) => {
        entry[key] = byType[key][i] || 0;
      });
      return entry;
    });

    const stack = d3.stack().keys(typeKeys);
    const series = stack(stackData);

    series.forEach((layer) => {
      const key = layer.key;
      g.selectAll(`.bar-${key}`)
        .data(layer)
        .enter()
        .append('rect')
        .attr('x', (d) => xScale(d.data.index))
        .attr('y', (d) => yRightScale(d[1]))
        .attr('width', xScale.bandwidth())
        .attr('height', (d) => yRightScale(d[0]) - yRightScale(d[1]))
        .attr('fill', TYPE_COLORS[key])
        .attr('opacity', 0.85)
        .attr('rx', 1);
    });

    // Invisible overlay rects for tooltip interaction
    g.selectAll('.hover-rect')
      .data(d3.range(n))
      .enter()
      .append('rect')
      .attr('x', (i) => xScale(i))
      .attr('y', 0)
      .attr('width', xScale.bandwidth())
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .attr('cursor', 'crosshair')
      .on('mousemove', (event, i) => {
        const containerRect = containerRef.current.getBoundingClientRect();
        const svgRect = svgRef.current.getBoundingClientRect();
        const mouseX = event.clientX - containerRect.left;
        const mouseY = event.clientY - containerRect.top;

        const breakdown = typeKeys
          .map(
            (key) =>
              `${TYPE_LABELS[key] || key}: ${byType[key][i] || 0}`
          )
          .join('\n');

        setTooltip({
          x: mouseX + 12,
          y: mouseY - 10,
          date: dates[i],
          cumulative: cumulativeCount[i],
          periodRate: rate[i],
          breakdown,
        });
      })
      .on('mouseleave', () => {
        setTooltip(null);
      });

    // X-axis
    const tickIndices = [];
    const maxTicks = 10;
    const step = Math.max(1, Math.ceil(n / maxTicks));
    for (let i = 0; i < n; i += step) {
      tickIndices.push(i);
    }

    const xAxis = d3
      .axisBottom(xScale)
      .tickValues(tickIndices)
      .tickFormat((i) => {
        const d = parsedDates[i];
        if (!d) return '';
        return d3.timeFormat('%b %d')(d);
      });

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(xAxis)
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-35)')
      .attr('text-anchor', 'end');

    g.selectAll('.domain').attr('stroke', '#d1d5db');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Left Y-axis (cumulative)
    const yLeftAxis = d3.axisLeft(yLeftScale).ticks(5);
    g.append('g')
      .call(yLeftAxis)
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#3b82f6');

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -38)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#3b82f6')
      .text('Cumulative Count');

    // Right Y-axis (rate)
    const yRightAxis = d3.axisRight(yRightScale).ticks(5);
    g.append('g')
      .attr('transform', `translate(${innerWidth},0)`)
      .call(yRightAxis)
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', innerWidth + 48)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Per-Period Rate');

    // Gridlines
    g.selectAll('.grid-line')
      .data(yLeftScale.ticks(5))
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', (d) => yLeftScale(d))
      .attr('y2', (d) => yLeftScale(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1)
      .lower();
  }, [data, isValid]);

  if (!isValid) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <h3 style={styles.title}>Anomaly Accumulation Monitor</h3>
          <p style={styles.subtitle}>
            Cumulative anomaly count and per-period rate
          </p>
        </div>
        <div style={styles.emptyState}>No anomaly data available</div>
      </div>
    );
  }

  const typeKeys = Object.keys(data.byType || {});

  return (
    <div style={styles.card}>
      <div
        style={{
          ...styles.header,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <h3 style={styles.title}>Anomaly Accumulation Monitor</h3>
          <p style={styles.subtitle}>
            Cumulative anomaly count and per-period rate
          </p>
        </div>
        {trendBadge && (
          <div
            style={{
              backgroundColor: trendBadge.bg,
              color: trendBadge.color,
              fontSize: '12px',
              fontWeight: 600,
              padding: '4px 12px',
              borderRadius: '12px',
              border: `1px solid ${trendBadge.color}33`,
              whiteSpace: 'nowrap',
            }}
          >
            {trendBadge.label}
          </div>
        )}
      </div>

      <div ref={containerRef} style={{ position: 'relative' }}>
        <svg
          ref={svgRef}
          viewBox="0 0 700 320"
          style={styles.svgContainer}
          preserveAspectRatio="xMidYMid meet"
        />

        {tooltip && (
          <div
            style={{
              ...styles.tooltip,
              left: tooltip.x,
              top: tooltip.y,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>
              {tooltip.date}
            </div>
            <div>Cumulative: {tooltip.cumulative}</div>
            <div>Period rate: {tooltip.periodRate}</div>
            <div
              style={{
                marginTop: '4px',
                borderTop: '1px solid #374151',
                paddingTop: '4px',
              }}
            >
              {tooltip.breakdown.split('\n').map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <div
            style={{
              ...styles.legendSwatch,
              backgroundColor: '#93c5fd',
              border: '1px solid #3b82f6',
            }}
          />
          <span>Cumulative</span>
        </div>
        <div style={styles.legendItem}>
          <div
            style={{
              width: '16px',
              height: '2px',
              backgroundColor: '#6366f1',
              borderTop: '2px dashed #6366f1',
              flexShrink: 0,
            }}
          />
          <span>Trend</span>
        </div>
        {typeKeys.map((key) => (
          <div key={key} style={styles.legendItem}>
            <div
              style={{
                ...styles.legendSwatch,
                backgroundColor: TYPE_COLORS[key] || '#9ca3af',
              }}
            />
            <span>{TYPE_LABELS[key] || key}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
